/**
 * BookingItinerarySection - Display and edit itinerary
 */
import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Route, Edit2, Plane, Train } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import { detectLocationType } from './constants';

const BookingItinerarySection = ({
  editData,
  setEditData,
  editMode,
  setEditMode,
  canEdit,
  booking  // Ajout pour accéder aux infos complètes du booking
}) => {
  const pickupLocationType = detectLocationType(editData.pickupAddress);
  const dropoffLocationType = detectLocationType(editData.dropoffAddress);

  return (
    <div 
      className={`bg-[#0B0B0B] rounded-lg p-3 sm:p-4 transition-all ${canEdit ? 'cursor-pointer hover:border-[#C9A24D]/50 border border-transparent active:bg-[#0B0B0B]/80' : ''}`}
      onClick={() => canEdit && editMode !== 'itinerary' && setEditMode('itinerary')}
      data-testid="itinerary-section"
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-[#E5E5E5]/50 text-[10px] sm:text-xs uppercase flex items-center gap-1.5 sm:gap-2">
          <Route className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C9A24D]" /> Itinéraire
        </p>
        {canEdit && editMode !== 'itinerary' && (
          <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C9A24D]/50" />
        )}
      </div>
      
      {editMode === 'itinerary' ? (
        <div className="space-y-2 sm:space-y-3" onClick={e => e.stopPropagation()}>
          <div>
            <label className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1 block">Départ</label>
            <AddressAutocomplete
              value={editData.pickupAddress}
              onChange={(val) => setEditData(prev => ({ ...prev, pickupAddress: val }))}
              onSelect={(data) => setEditData(prev => ({ 
                ...prev, 
                pickupAddress: data.address,
                pickupCoords: { lat: data.latitude, lng: data.longitude }
              }))}
              placeholder="Adresse de départ"
              testId="pickup-address-input"
            />
            {/* Conditionally show flight/train input for pickup */}
            {pickupLocationType === 'airport' && (
              <div className="flex items-center gap-2 mt-2">
                <Plane className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400 flex-shrink-0" />
                <Input
                  value={editData.flightNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, flightNumber: e.target.value.toUpperCase() }))}
                  placeholder="N° vol (ex: AF1234)"
                  className="bg-[#1F1F1F] border-cyan-400/30 text-[#E5E5E5] text-xs sm:text-sm flex-1 h-8 sm:h-9"
                  data-testid="pickup-flight-number"
                />
              </div>
            )}
            {pickupLocationType === 'train' && (
              <div className="flex items-center gap-2 mt-2">
                <Train className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                <Input
                  value={editData.trainNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, trainNumber: e.target.value.toUpperCase() }))}
                  placeholder="N° train (ex: TGV6201)"
                  className="bg-[#1F1F1F] border-orange-400/30 text-[#E5E5E5] text-xs sm:text-sm flex-1 h-8 sm:h-9"
                  data-testid="pickup-train-number"
                />
              </div>
            )}
          </div>
          <div>
            <label className="text-[#E5E5E5]/40 text-[10px] sm:text-xs mb-1 block">Arrivée</label>
            <AddressAutocomplete
              value={editData.dropoffAddress}
              onChange={(val) => setEditData(prev => ({ ...prev, dropoffAddress: val }))}
              onSelect={(data) => setEditData(prev => ({ 
                ...prev, 
                dropoffAddress: data.address,
                dropoffCoords: { lat: data.latitude, lng: data.longitude }
              }))}
              placeholder="Adresse d'arrivée"
              testId="dropoff-address-input"
            />
            {/* Conditionally show flight/train input for dropoff */}
            {dropoffLocationType === 'airport' && (
              <div className="flex items-center gap-2 mt-2">
                <Plane className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-400 flex-shrink-0" />
                <Input
                  value={editData.departureFlightNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, departureFlightNumber: e.target.value.toUpperCase() }))}
                  placeholder="N° vol départ (ex: AF5678)"
                  className="bg-[#1F1F1F] border-cyan-400/30 text-[#E5E5E5] text-xs sm:text-sm flex-1 h-8 sm:h-9"
                  data-testid="dropoff-flight-number"
                />
              </div>
            )}
            {dropoffLocationType === 'train' && (
              <div className="flex items-center gap-2 mt-2">
                <Train className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-400 flex-shrink-0" />
                <Input
                  value={editData.departureTrainNumber}
                  onChange={(e) => setEditData(prev => ({ ...prev, departureTrainNumber: e.target.value.toUpperCase() }))}
                  placeholder="N° train (ex: TGV6202)"
                  className="bg-[#1F1F1F] border-orange-400/30 text-[#E5E5E5] text-xs sm:text-sm flex-1 h-8 sm:h-9"
                  data-testid="dropoff-train-number"
                />
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditMode(null)}
            className="text-[#C9A24D]"
          >
            Fermer
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Départ */}
          <div className="flex items-start gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500 mt-1.5 sm:mt-1 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[#E5E5E5] text-xs sm:text-sm break-words">{editData.pickupAddress || '-'}</p>
              {editData.flightNumber && (
                <p className="text-cyan-400 text-[10px] sm:text-xs flex items-center gap-1 mt-0.5">
                  <Plane className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Vol {editData.flightNumber}
                </p>
              )}
              {editData.trainNumber && (
                <p className="text-orange-400 text-[10px] sm:text-xs flex items-center gap-1 mt-0.5">
                  <Train className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Train {editData.trainNumber}
                </p>
              )}
            </div>
          </div>
          
          {/* Étapes intermédiaires depuis booking.itinerary.waypoints */}
          {booking?.itinerary?.waypoints && booking.itinerary.waypoints.length > 2 && (
            <>
              {booking.itinerary.waypoints
                .filter(wp => wp.type === 'intermediate')
                .map((waypoint, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#C9A24D] mt-1.5 sm:mt-1 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[#E5E5E5]/80 text-xs sm:text-sm break-words">{waypoint.address}</p>
                    </div>
                  </div>
                ))}
            </>
          )}
          
          {/* Arrivée */}
          <div className="flex items-start gap-2">
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500 mt-1.5 sm:mt-1 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[#E5E5E5] text-xs sm:text-sm break-words">{editData.dropoffAddress || '-'}</p>
              {editData.departureFlightNumber && (
                <p className="text-cyan-400 text-[10px] sm:text-xs flex items-center gap-1 mt-0.5">
                  <Plane className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Vol {editData.departureFlightNumber}
                </p>
              )}
              {editData.departureTrainNumber && (
                <p className="text-orange-400 text-[10px] sm:text-xs flex items-center gap-1 mt-0.5">
                  <Train className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Train {editData.departureTrainNumber}
                </p>
              )}
            </div>
          </div>
          
          {/* Distance et durée */}
          {(booking?.itinerary?.total_distance_km > 0 || booking?.itinerary?.estimated_duration_minutes > 0) && (
            <div className="flex gap-3 pt-2 border-t border-[#C9A24D]/20 text-[10px] sm:text-xs text-[#E5E5E5]/60">
              {booking.itinerary.total_distance_km > 0 && (
                <span>📍 {booking.itinerary.total_distance_km.toFixed(1)} km</span>
              )}
              {booking.itinerary.estimated_duration_minutes > 0 && (
                <span>⏱️ {Math.floor(booking.itinerary.estimated_duration_minutes / 60)}h{booking.itinerary.estimated_duration_minutes % 60}min</span>
              )}
            </div>
          )}
          
          {/* Chauffeur et véhicule attribués */}
          {(booking?.driver_info?.assigned || booking?.vehicle_info?.assigned) && (
            <div className="pt-2 border-t border-[#C9A24D]/20 space-y-1.5">
              {booking.driver_info?.assigned && booking.driver_info?.name && (
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <span className="text-[#E5E5E5]/40">Chauffeur:</span>
                  <span className="text-[#C9A24D] font-medium">{booking.driver_info.name}</span>
                </div>
              )}
              {booking.vehicle_info?.assigned && booking.vehicle_info?.license_plate && (
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <span className="text-[#E5E5E5]/40">Véhicule:</span>
                  <span className="text-[#C9A24D] font-mono font-medium">{booking.vehicle_info.license_plate}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingItinerarySection;
